import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, Plus, Check, X, Send, Target,
  Calendar as CalIcon, MessageSquare, Sparkles, Loader2,
  StickyNote, Camera, Maximize2, Minimize2, Trash2, Settings, GripVertical
} from 'lucide-react';
import strategyMd from '../strategy.md?raw';
import {
  notifyItemAdded, bannerShouldShow, enable, dismissBanner,
  pushSupported, permission, ID, disable, resync, sendTest,
} from './push.js';

// ---------- Date utilities ----------
const pad = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDate = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const startOfWeek = (d) => {
  const r = new Date(d); const day = (r.getDay() + 6) % 7;
  r.setDate(r.getDate() - day); r.setHours(0, 0, 0, 0); return r;
};
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const addMonths = (d, n) => { const r = new Date(d); r.setMonth(r.getMonth() + n); return r; };
const sameDay = (a, b) => fmtDate(a) === fmtDate(b);
const weekNum = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
};
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_SHORT = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// ---------- Default state ----------
const DEFAULT_STATE = {
  goals: [],   // array of { id, text, addedAt }
  days: {},
  chats: [],
  noteGroups: [],   // array of { id, title, notes: [{id, text}] }
  theme: 'sage',
};

// ---------- Themes ----------
const THEMES = {
  sage:   { name: 'Sage',   body: '#F2EFE6', card: '#EAE6D8', tint: '#E1E7D0', tintAlpha: 'rgba(225, 231, 208, 0.88)', accent: '#C9D2B4', strong: '#2D5043' },
  blue:   { name: 'Blue',   body: '#EEF4FA', card: '#E4ECF4', tint: '#DAE5EF', tintAlpha: 'rgba(218, 229, 239, 0.88)', accent: '#BBCDDE', strong: '#0369A1' },
  rust:   { name: 'Rust',   body: '#FBF1EB', card: '#F5E5DA', tint: '#EFD5C4', tintAlpha: 'rgba(239, 213, 196, 0.88)', accent: '#DEB7A0', strong: '#B0501F' },
  wine:   { name: 'Wine',   body: '#F7ECEF', card: '#EFDDE3', tint: '#E4C4CE', tintAlpha: 'rgba(228, 196, 206, 0.88)', accent: '#C99AA7', strong: '#9B2C44' },
  forest: { name: 'Forest', body: '#EFF6F1', card: '#E5EFE7', tint: '#D2E5D7', tintAlpha: 'rgba(210, 229, 215, 0.88)', accent: '#A4C7AB', strong: '#15803D' },
};

function normalizeGoals(g) {
  if (Array.isArray(g)) return g;
  return [];
}

function normalizeNotes(n) {
  if (Array.isArray(n)) return n;
  if (typeof n === 'string' && n.trim()) {
    return n.split('\n').map(l => l.trim()).filter(Boolean)
      .map((text, i) => ({ id: Date.now() + i + Math.random(), text }));
  }
  return [];
}

// Merges legacy `notes` (array or string) into a single migrated group.
function normalizeNoteGroups(s) {
  if (Array.isArray(s.noteGroups)) {
    return s.noteGroups.map(g => ({
      id: g.id ?? Date.now() + Math.random(),
      title: typeof g.title === 'string' ? g.title : '',
      notes: normalizeNotes(g.notes),
    }));
  }
  const legacy = normalizeNotes(s.notes);
  if (legacy.length > 0) {
    return [{ id: Date.now() + Math.random(), title: 'Notes', notes: legacy }];
  }
  return [];
}

function normalizeChat(c) {
  if (Array.isArray(c)) return c;
  if (c && typeof c === 'object') {
    const flat = [];
    for (const [date, msgs] of Object.entries(c)) {
      if (!Array.isArray(msgs)) continue;
      for (const m of msgs) flat.push({ ...m, date: m.date || date });
    }
    flat.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    return flat;
  }
  return [];
}

// ---------- Storage (localStorage cache + Netlify Blobs sync) ----------
const STORAGE_KEY = 'operator_state_v1';
const SYNC_URL = '/.netlify/functions/state';

function loadLocal() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const merged = { ...DEFAULT_STATE, ...JSON.parse(raw) };
      merged.goals = normalizeGoals(merged.goals);
      merged.noteGroups = normalizeNoteGroups(merged);
      delete merged.notes;
      merged.chats = normalizeChat(merged.chats);
      return merged;
    }
  } catch (e) { /* first run or quota */ }
  return DEFAULT_STATE;
}
function saveLocal(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch (e) { console.error('local save failed', e); }
}

async function fetchRemote() {
  const r = await fetch(SYNC_URL, { method: 'GET', cache: 'no-store' });
  if (!r.ok) throw new Error(`GET ${r.status}`);
  return r.json(); // { state, updatedAt }
}
async function pushRemote(state) {
  const r = await fetch(SYNC_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(state),
  });
  if (!r.ok) throw new Error(`PUT ${r.status}`);
  return r.json();
}

// Real data = remote blob has days, chats, notes, or goals populated.
function hasRealData(s) {
  if (!s || typeof s !== 'object') return false;
  const daysCount = s.days ? Object.keys(s.days).length : 0;
  let chatsCount = 0;
  if (Array.isArray(s.chats)) chatsCount = s.chats.length;
  else if (s.chats && typeof s.chats === 'object') {
    chatsCount = Object.values(s.chats).reduce((a, v) => a + (Array.isArray(v) ? v.length : 0), 0);
  }
  const legacyNotes = Array.isArray(s.notes)
    ? s.notes.length > 0
    : (typeof s.notes === 'string' && s.notes.trim().length > 0);
  const hasGroups = Array.isArray(s.noteGroups) && s.noteGroups.some(g => (g.notes || []).length > 0 || (g.title || '').trim().length > 0);
  const hasGoals = Array.isArray(s.goals) && s.goals.length > 0;
  return daysCount + chatsCount > 0 || legacyNotes || hasGroups || hasGoals;
}

// ---------- Claude chat (via Netlify Function proxy) ----------
async function callClaude(systemPrompt, messages, options = {}) {
  const body = { system: systemPrompt, messages };
  if (options.maxTokens) body.max_tokens = options.maxTokens;
  const response = await fetch('/.netlify/functions/claude', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`API ${response.status}${text ? ` — ${text}` : ''}`);
  }
  const data = await response.json();
  return data.content.filter(b => b.type === 'text').map(b => b.text).join('\n');
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      const comma = typeof r === 'string' ? r.indexOf(',') : -1;
      resolve(comma >= 0 ? r.slice(comma + 1) : r);
    };
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

async function scanCalendarImage({ base64, mediaType, existingEvents, contextDateKey }) {
  const existingBlock = existingEvents.length
    ? existingEvents.map(e => `- ${e.date}: ${e.text}`).join('\n')
    : '(none)';
  const system = `You extract events from a photo of a paper calendar and return ONLY JSON — no prose, no markdown fences.

Today is ${contextDateKey}. Use month headers, weekday columns, and day numbers in the photo to anchor every event to an absolute ISO date (YYYY-MM-DD). If the photo covers a future or past month, infer the year from context.

The family already has these events in their digital calendar. The paper calendar is usually the SAME calendar plus a few new additions. Return ONLY events visible in the photo that are NOT already in this list. Use fuzzy matching — "Lily recital 8pm" on 2026-05-01 is the same as "Lily's recital @ 20:00" on 2026-05-01, skip it.

EXISTING EVENTS:
${existingBlock}

Output a JSON array only. Each item:
  { "date": "YYYY-MM-DD", "title": "Event description" }
For a multi-day event add an endDate:
  { "date": "YYYY-MM-DD", "endDate": "YYYY-MM-DD", "title": "..." }

If no new events, return []. Never wrap in \`\`\`.`;

  const messages = [{
    role: 'user',
    content: [
      { type: 'image', source: { type: 'base64', media_type: mediaType, data: base64 } },
      { type: 'text', text: 'Extract new events from this calendar photo.' },
    ],
  }];
  const raw = await callClaude(system, messages, { maxTokens: 3000 });
  const cleaned = raw.replace(/^```(?:json)?\s*/i, '').replace(/```\s*$/i, '').trim();
  const match = cleaned.match(/\[[\s\S]*\]/);
  const jsonText = match ? match[0] : cleaned;
  let parsed;
  try { parsed = JSON.parse(jsonText); }
  catch { throw new Error('Claude did not return valid JSON'); }
  if (!Array.isArray(parsed)) throw new Error('Expected a JSON array');
  return parsed;
}

function buildSystemPrompt(state, today) {
  const todayKey = fmtDate(today);
  const day = state.days[todayKey] || { items: [], notes: '' };
  const goals = Array.isArray(state.goals) ? state.goals : [];
  const recent = Object.entries(state.days)
    .filter(([k]) => k < todayKey)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 3)
    .map(([k, v]) => `${k}: ${v.items.filter(i => i.done).length}/${v.items.length} done${v.notes ? ' — ' + v.notes : ''}`)
    .join('\n');

  return `You are the Lloyd family's operator AI. This chat is a single continuous thread across all dates — you see history from prior days. Messages prefixed like "[2026-04-20] ..." came from that date; un-prefixed messages are from today's selected context. You can freely reference and build on things said on other dates. The family uses this to plan life, not business — be direct and useful, warmer than strict, and match Johnny's pace. Don't cheerlead, but don't be clinical either.

Context date (the day currently selected): ${todayKey}

FAMILY GOALS:
${goals.length ? goals.map(g => `- ${g.text}`).join('\n') : 'none yet'}

TODAY (${todayKey}):
Items: ${day.items.length ? day.items.map(i => `${i.done?'✓':'○'} ${i.text}`).join(' | ') : 'none yet'}
${day.notes ? `Notes: ${day.notes}` : ''}

RECENT DAYS:
${recent || 'no prior days logged'}

STYLE:
- Direct, concise, no caveats. Warm but not soft.
- Push back when something's off, but skip the lectures.
- Daily check-ins should be short.
- When suggesting events, format each as a markdown list item with an ISO date in brackets: "- [YYYY-MM-DD] Description". For multi-day events use "- [YYYY-MM-DD..YYYY-MM-DD] Description". The bracket lets the app one-click add the event to the right day(s). Do not add the date again in the description.
- Address him as "you". Don't use his name repeatedly.

STRATEGIC CONTEXT (living doc — Johnny edits as plans shift):
${strategyMd}`;
}

const MONTH_MAP = {
  jan:0, january:0, feb:1, february:1, mar:2, march:2, apr:3, april:3,
  may:4, jun:5, june:5, jul:6, july:6, aug:7, august:7,
  sep:8, sept:8, september:8, oct:9, october:9, nov:10, november:10, dec:11, december:11,
};
const MONTH_KEYS = Object.keys(MONTH_MAP).join('|');
const DAY_NAME_PAT = 'mon|tue|tues|wed|thu|thur|thurs|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday';

function datesBetween(start, end) {
  const out = [];
  const cur = new Date(start); cur.setHours(0, 0, 0, 0);
  const lim = new Date(end); lim.setHours(0, 0, 0, 0);
  // cap at ~60 days to avoid runaway ranges
  let safety = 60;
  while (cur <= lim && safety-- > 0) {
    out.push(fmtDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function parseItemDates(raw, ctxDate) {
  // 1) ISO bracket: [YYYY-MM-DD] or [YYYY-MM-DD..YYYY-MM-DD]
  const iso = raw.match(/^\[(\d{4}-\d{2}-\d{2})(?:\.\.(\d{4}-\d{2}-\d{2}))?\]\s*[-—–:]?\s*(.+)$/);
  if (iso) {
    const start = parseDate(iso[1]);
    const end = iso[2] ? parseDate(iso[2]) : start;
    return { text: iso[3].trim(), dates: datesBetween(start, end) };
  }

  // 2) English date fallback: "Fri May 1 – Sat May 2, 16:00 — Description"
  const re = new RegExp(
    `^(?:(?:${DAY_NAME_PAT})\\s+)?` +
    `(${MONTH_KEYS})\\s+(\\d{1,2})` +
    `(?:\\s*[–—-]\\s*(?:(?:${DAY_NAME_PAT})\\s+)?(?:(${MONTH_KEYS})\\s+)?(\\d{1,2}))?` +
    `(?:,?\\s*(\\d{1,2}:\\d{2}))?` +
    `\\s*[—–-]\\s*(.+)$`,
    'i'
  );
  const m = raw.match(re);
  if (m) {
    const [, mon1, d1, mon2, d2, time, rest] = m;
    const year = ctxDate.getFullYear();
    const mIdx1 = MONTH_MAP[mon1.toLowerCase()];
    const start = new Date(year, mIdx1, parseInt(d1, 10));
    // If start is more than 6 months before ctxDate, assume next year
    if ((start - ctxDate) / (86400000 * 30) < -6) start.setFullYear(year + 1);
    let end = start;
    if (d2) {
      const mIdx2 = mon2 ? MONTH_MAP[mon2.toLowerCase()] : start.getMonth();
      end = new Date(start.getFullYear(), mIdx2, parseInt(d2, 10));
      if (end < start) end.setFullYear(end.getFullYear() + 1);
    }
    const body = time ? `${time} ${rest.trim()}` : rest.trim();
    return { text: body, dates: datesBetween(start, end) };
  }

  return { text: raw, dates: [] };
}

function extractListItems(text, contextDateKey) {
  const ctxDate = contextDateKey ? parseDate(contextDateKey) : new Date();
  const lines = text.split('\n');
  const items = [];
  for (const line of lines) {
    const m = line.match(/^\s*[-*]\s+(.+?)$/) || line.match(/^\s*\d+[.)]\s+(.+?)$/);
    if (!m) continue;
    const raw = m[1].replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1').trim();
    if (raw.length <= 3 || raw.length >= 200) continue;
    const parsed = parseItemDates(raw, ctxDate);
    if (parsed.text.length <= 1) continue;
    items.push(parsed);
  }
  return items;
}

function formatDateBadge(dates) {
  if (!dates || !dates.length) return '';
  const first = parseDate(dates[0]);
  if (dates.length === 1) return `${MONTHS[first.getMonth()]} ${first.getDate()}`;
  const last = parseDate(dates[dates.length - 1]);
  if (first.getMonth() === last.getMonth()) return `${MONTHS[first.getMonth()]} ${first.getDate()}–${last.getDate()}`;
  return `${MONTHS[first.getMonth()]} ${first.getDate()} – ${MONTHS[last.getMonth()]} ${last.getDate()}`;
}

// ---------- Component ----------
export default function Operator() {
  const [state, setState] = useState(() => loadLocal());
  const [view, setView] = useState('week');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);
  const [anchor, setAnchor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(() => {
    if (typeof window !== 'undefined') {
      const param = new URLSearchParams(window.location.search).get('day');
      if (param && /^\d{4}-\d{2}-\d{2}$/.test(param)) return param;
    }
    return fmtDate(new Date());
  });
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState('');
  const [mobilePanel, setMobilePanel] = useState('cal');
  const [enableDrag, setEnableDrag] = useState(() =>
    typeof window !== 'undefined' && !window.matchMedia('(pointer: coarse)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(pointer: coarse)');
    const update = () => setEnableDrag(!mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  // Phone layout (< md): week view becomes an infinite vertical list, day panel is swipeable.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  useEffect(() => {
    // The day panel uses history entries for back-navigation; the browser's own scroll
    // restoration on popstate would override the week list's restore/centre logic.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);
  const [weekScrollReq, setWeekScrollReq] = useState(null);
  const weekBarRef = useRef(null);
  const headerRef = useRef(null);
  useEffect(() => {
    // Expose the sticky header height as --hdr-h so the mobile week bar can stick right under it.
    const el = headerRef.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--hdr-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const panelHistoryRef = useRef(false);
  // sync: 'loading' on mount, 'saved' when in sync, 'saving' during push, 'error' on failure, 'offline' when fetch fails
  const [syncStatus, setSyncStatus] = useState('loading');
  const chatEndRef = useRef(null);
  const hydratedRef = useRef(false);       // gate writes until initial remote load completes
  const pushTimerRef = useRef(null);
  const lastPushedRef = useRef('');

  const [showBanner, setShowBanner] = useState(false);
  const [bannerUser, setBannerUser] = useState(null); // 'johnny' | 'elleke' | null
  const [bannerError, setBannerError] = useState(null);
  const [notifState, setNotifState] = useState(() => ({
    supported: pushSupported(),
    perm: permission(),
    userId: ID.get(),
    enabled: localStorage.getItem('tnf.pushEnabled') === 'true',
  }));
  const [notifBusy, setNotifBusy] = useState(false);
  const [notifError, setNotifError] = useState(null);

  const today = useMemo(() => new Date(), []);
  const todayKey = fmtDate(today);
  const currentTheme = THEMES[state.theme] || THEMES.sage;
  const setTheme = (key) => setState(s => ({ ...s, theme: key }));

  useEffect(() => {
    if (!settingsOpen) return;
    const onDown = (e) => { if (settingsRef.current && !settingsRef.current.contains(e.target)) setSettingsOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [settingsOpen]);

  useEffect(() => {
    setShowBanner(bannerShouldShow());
  }, []);

  useEffect(() => {
    // When clicked from a notification while the app is already open, the SW
    // calls .navigate() which changes the URL without remounting. Listen for
    // popstate and re-read.
    const onPop = () => {
      const param = new URLSearchParams(window.location.search).get('day');
      if (param && /^\d{4}-\d{2}-\d{2}$/.test(param)) setSelectedDay(param);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    // On mount, if we deep-linked to a day, also align the week-view anchor.
    const param = new URLSearchParams(window.location.search).get('day');
    if (param && /^\d{4}-\d{2}-\d{2}$/.test(param)) {
      const d = parseDate(param);
      if (d) setAnchor(d);
    }
  }, []); // mount-only

  const onEnableBanner = async () => {
    setBannerError(null);
    if (!bannerUser) { setBannerError('Pick who this is.'); return; }
    try {
      await enable(bannerUser);
      setShowBanner(false);
      refreshNotifState();
    } catch (e) {
      setBannerError(e.message || 'Failed to enable.');
    }
  };

  const onDismissBanner = () => {
    dismissBanner();
    setShowBanner(false);
  };

  const refreshNotifState = () => setNotifState({
    supported: pushSupported(),
    perm: permission(),
    userId: ID.get(),
    enabled: localStorage.getItem('tnf.pushEnabled') === 'true',
  });

  const handleEnableAs = async (userId) => {
    setNotifError(null); setNotifBusy(true);
    try { await enable(userId); refreshNotifState(); }
    catch (e) { setNotifError(e.message); }
    finally { setNotifBusy(false); }
  };

  const handleDisable = async () => {
    setNotifError(null); setNotifBusy(true);
    try { await disable(); refreshNotifState(); }
    catch (e) { setNotifError(e.message); }
    finally { setNotifBusy(false); }
  };

  const [notifTestMsg, setNotifTestMsg] = useState(null);
  const handleSendTest = async () => {
    setNotifError(null); setNotifTestMsg(null); setNotifBusy(true);
    try {
      await resync({ force: true });
      await sendTest();
      setNotifTestMsg('Sent — it should arrive in a few seconds.');
    } catch (e) { setNotifError(e.message); }
    finally { setNotifBusy(false); }
  };

  // Keep this device registered for push: on open and whenever the app returns to the foreground.
  // Fixes the "UI says enabled but the server dropped the subscription" failure mode.
  useEffect(() => {
    const run = () => { resync().then(refreshNotifState).catch((e) => console.warn('push resync failed', e)); };
    run();
    const onVis = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: hydrate from remote. Remote wins if it has real data, else push local seed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { state: remote } = await fetchRemote();
        if (cancelled) return;
        if (hasRealData(remote)) {
          const merged = { ...DEFAULT_STATE, ...remote };
          merged.goals = normalizeGoals(merged.goals);
          merged.noteGroups = normalizeNoteGroups(merged);
          delete merged.notes;
          merged.chats = normalizeChat(merged.chats);
          setState(merged);
          saveLocal(merged);
          lastPushedRef.current = JSON.stringify(merged);
        } else {
          // Remote empty → seed it with whatever we had locally (or DEFAULT_STATE).
          const local = loadLocal();
          await pushRemote(local);
          lastPushedRef.current = JSON.stringify(local);
        }
        setSyncStatus('saved');
      } catch (e) {
        console.warn('remote hydrate failed, using local cache', e);
        setSyncStatus('offline');
      } finally {
        hydratedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // On state change: always cache locally; debounced push to remote (after hydrate).
  useEffect(() => {
    saveLocal(state);
    if (!hydratedRef.current) return;

    const serialized = JSON.stringify(state);
    if (serialized === lastPushedRef.current) return;

    setSyncStatus(s => (s === 'offline' ? 'offline' : 'saving'));
    clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(async () => {
      try {
        await pushRemote(state);
        lastPushedRef.current = serialized;
        setSyncStatus('saved');
      } catch (e) {
        console.warn('remote push failed', e);
        setSyncStatus('error');
      }
    }, 1200);

    return () => clearTimeout(pushTimerRef.current);
  }, [state]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [state.chats, selectedDay]);

  const updateDay = (key, updater) => {
    setState(s => {
      const prev = s.days[key] || { items: [], notes: '', hours: {} };
      return { ...s, days: { ...s.days, [key]: updater(prev) } };
    });
  };

  const addItem = (key, text, stream = null) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    updateDay(key, d => ({ ...d, items: [...(d.items || []), { id: Date.now() + Math.random(), text: trimmed, done: false, stream }] }));
    notifyItemAdded({ dateKey: key, text: trimmed, stream });
  };
  const deleteItem = (key, id) => {
    updateDay(key, d => ({ ...d, items: d.items.filter(i => i.id !== id) }));
  };
  const editItem = (key, id, text) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    updateDay(key, d => ({
      ...d,
      items: (d.items || []).map(i => i.id === id ? { ...i, text: trimmed } : i),
    }));
  };
  const reorderItems = (key, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    updateDay(key, d => {
      const items = [...(d.items || [])];
      if (fromIndex < 0 || fromIndex >= items.length) return d;
      const [moved] = items.splice(fromIndex, 1);
      const insertAt = Math.max(0, Math.min(toIndex, items.length));
      items.splice(insertAt, 0, moved);
      return { ...d, items };
    });
  };
  const moveItem = (fromKey, toKey, id) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setState(s => {
      const fromDay = s.days[fromKey] || { items: [] };
      const item = (fromDay.items || []).find(i => i.id === id);
      if (!item) return s;
      const toDay = s.days[toKey] || { items: [], notes: '', hours: {} };
      return {
        ...s,
        days: {
          ...s.days,
          [fromKey]: { ...fromDay, items: (fromDay.items || []).filter(i => i.id !== id) },
          [toKey]: { ...toDay, items: [...(toDay.items || []), item] },
        },
      };
    });
  };
  const setNotes = (key, notes) => updateDay(key, d => ({ ...d, notes }));

  const updateGoalsList = (fn) => setState(s => ({
    ...s,
    goals: fn(Array.isArray(s.goals) ? s.goals : []),
  }));
  const clearGoals = () => {
    if (!window.confirm('Clear all goals?')) return;
    setState(s => ({ ...s, goals: [] }));
  };
  const addNoteGroup = () => {
    const id = Date.now() + Math.random();
    setState(s => ({
      ...s,
      noteGroups: [...(Array.isArray(s.noteGroups) ? s.noteGroups : []), { id, title: '', notes: [] }],
    }));
  };
  const deleteNoteGroup = (id) => {
    if (!window.confirm('Delete this list and all its notes?')) return;
    setState(s => ({
      ...s,
      noteGroups: (s.noteGroups || []).filter(g => g.id !== id),
    }));
  };
  const setNoteGroupTitle = (id, title) => setState(s => ({
    ...s,
    noteGroups: (s.noteGroups || []).map(g => g.id === id ? { ...g, title } : g),
  }));
  const updateNotesInGroup = (id, fn) => setState(s => ({
    ...s,
    noteGroups: (s.noteGroups || []).map(g =>
      g.id === id ? { ...g, notes: fn(Array.isArray(g.notes) ? g.notes : []) } : g
    ),
  }));
  const clearNotesInGroup = (id) => {
    if (!window.confirm('Clear all notes in this list?')) return;
    setState(s => ({
      ...s,
      noteGroups: (s.noteGroups || []).map(g => g.id === id ? { ...g, notes: [] } : g),
    }));
  };
  const clearChats = () => {
    if (!window.confirm('Clear chat thread?')) return;
    setState(s => ({ ...s, chats: [] }));
  };

  const sendMessage = async () => {
    const txt = chatInput.trim();
    if (!txt || chatLoading) return;
    setChatError('');
    const now = Date.now();
    const userMsg = { role: 'user', content: txt, ts: now, date: selectedDay };
    const nextChats = [...(state.chats || []), userMsg];
    setState(s => ({ ...s, chats: nextChats }));
    setChatInput('');
    setChatLoading(true);
    try {
      const WINDOW = 40;
      const windowed = nextChats.slice(-WINDOW);
      const messagesForAPI = windowed.map(m => ({
        role: m.role,
        content: m.date && m.date !== selectedDay ? `[${m.date}] ${m.content}` : m.content,
      }));
      const systemPrompt = buildSystemPrompt(state, parseDate(selectedDay));
      const reply = await callClaude(systemPrompt, messagesForAPI);
      const asst = { role: 'assistant', content: reply, ts: Date.now(), date: selectedDay };
      setState(s => ({ ...s, chats: [...(s.chats || []), asst] }));
    } catch (e) {
      setChatError(e.message || 'Something went wrong.');
    } finally {
      setChatLoading(false);
    }
  };

  const scanCalendar = async (file) => {
    if (!file || scanLoading) return;
    setScanError('');
    setScanLoading(true);
    try {
      const base64 = await fileToBase64(file);
      const mediaType = (file.type && file.type.startsWith('image/')) ? file.type : 'image/jpeg';

      const ctx = parseDate(selectedDay);
      const windowStart = fmtDate(addDays(ctx, -90));
      const windowEnd = fmtDate(addDays(ctx, 180));
      const existing = [];
      for (const [k, d] of Object.entries(state.days)) {
        if (k < windowStart || k > windowEnd) continue;
        for (const it of (d.items || [])) existing.push({ date: k, text: it.text });
      }

      const events = await scanCalendarImage({
        base64, mediaType,
        existingEvents: existing,
        contextDateKey: selectedDay,
      });

      const normalize = s => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const existingByDate = new Map();
      for (const e of existing) {
        const key = e.date;
        if (!existingByDate.has(key)) existingByDate.set(key, []);
        existingByDate.get(key).push(normalize(e.text));
      }

      const added = [];
      const skipped = [];
      setState(s => {
        const nextDays = { ...s.days };
        for (const ev of events) {
          if (!ev || !ev.date || !ev.title) continue;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(ev.date)) continue;
          const start = parseDate(ev.date);
          const end = ev.endDate && /^\d{4}-\d{2}-\d{2}$/.test(ev.endDate) ? parseDate(ev.endDate) : start;
          const dates = datesBetween(start, end);
          if (!dates.length) continue;
          const evNorm = normalize(ev.title);
          const existingNorms = existingByDate.get(dates[0]) || [];
          const isDup = existingNorms.some(n => n && (n.includes(evNorm) || evNorm.includes(n)));
          if (isDup) { skipped.push(ev); continue; }
          for (const k of dates) {
            const prev = nextDays[k] || { items: [], notes: '', hours: {} };
            nextDays[k] = {
              ...prev,
              items: [...(prev.items || []), { id: Date.now() + Math.random() + dates.indexOf(k), text: ev.title, done: false, stream: null }],
            };
          }
          added.push({ ...ev, dates });
        }
        return { ...s, days: nextDays };
      });

      const lines = [];
      if (added.length) {
        lines.push(`Scanned your calendar — added ${added.length} new event${added.length === 1 ? '' : 's'}:`);
        for (const a of added) {
          const badge = formatDateBadge(a.dates);
          lines.push(`  • ${badge} — ${a.title}`);
        }
      } else {
        lines.push(`Scanned your calendar — nothing new to add.`);
      }
      if (skipped.length) lines.push(`\nSkipped ${skipped.length} already on the calendar.`);
      const msg = { role: 'assistant', content: lines.join('\n'), ts: Date.now(), date: selectedDay };
      setState(s => ({ ...s, chats: [...(s.chats || []), msg] }));
    } catch (e) {
      setScanError(e.message || 'Scan failed');
    } finally {
      setScanLoading(false);
    }
  };

  const navigatePrev = () => {
    if (view === 'week') setAnchor(addDays(anchor, -7));
    else if (view === 'month') setAnchor(addMonths(anchor, -1));
    else setAnchor(addMonths(anchor, -12));
  };
  const navigateNext = () => {
    if (view === 'week') setAnchor(addDays(anchor, 7));
    else if (view === 'month') setAnchor(addMonths(anchor, 1));
    else setAnchor(addMonths(anchor, 12));
  };
  const jumpToday = () => { setAnchor(new Date()); setSelectedDay(todayKey); setMobilePanel('today'); };
  const selectDay = (key) => {
    setSelectedDay(key);
    setMobilePanel('today');
    if (!panelHistoryRef.current && typeof window !== 'undefined') {
      try { window.history.pushState({ tnPanel: true }, ''); panelHistoryRef.current = true; } catch {}
    }
  };
  const closeDayPanel = () => {
    if (panelHistoryRef.current && typeof window !== 'undefined') {
      try { window.history.back(); return; } catch {}
    }
    setMobilePanel('cal');
  };

  useEffect(() => {
    const onPop = () => {
      if (panelHistoryRef.current) {
        panelHistoryRef.current = false;
        setMobilePanel('cal');
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const viewLabel = useMemo(() => {
    if (view === 'week') {
      const s = addDays(anchor, -1); const e = addDays(s, 13);
      return `Weeks ${weekNum(s)}–${weekNum(e)} · ${MONTHS[s.getMonth()]} ${s.getDate()} – ${MONTHS[e.getMonth()]} ${e.getDate()}`;
    }
    if (view === 'month') return `${MONTHS_FULL[anchor.getMonth()]} ${anchor.getFullYear()}`;
    return `${anchor.getFullYear()}`;
  }, [view, anchor]);

  return (
    <div className="min-h-screen text-stone-900" style={{
      '--theme-body': currentTheme.body,
      '--theme-card': currentTheme.card,
      '--theme-tint': currentTheme.tint,
      '--theme-tint-alpha': currentTheme.tintAlpha,
      '--theme-accent': currentTheme.accent,
      '--theme-strong': currentTheme.strong,
      backgroundColor: 'var(--theme-body)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif'
    }}>
      <style>{`
        :root { --accent: #1a3a2e; --accent-soft: #e7ede9; }
        .serif { font-family: "Iowan Old Style", "Palatino Linotype", Palatino, "URW Palladio L", Georgia, serif; font-feature-settings: "onum" 1; }
        .scrollbar-thin::-webkit-scrollbar { width: 6px; height: 6px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #d6d3d1; border-radius: 3px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .fade-in { animation: fade 0.3s ease; }
        @keyframes fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        .slide-from-right { animation: slideFromRight 0.22s ease-out; }
        .slide-from-left { animation: slideFromLeft 0.22s ease-out; }
        @keyframes slideFromRight { from { opacity: 0; transform: translateX(32px); } to { opacity: 1; transform: none; } }
        @keyframes slideFromLeft { from { opacity: 0; transform: translateX(-32px); } to { opacity: 1; transform: none; } }
        .theme-strong { color: var(--theme-strong); }
        .theme-hover:hover { color: var(--theme-strong); }
      `}</style>

      {showBanner && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-3">
          <div className="max-w-[1600px] mx-auto flex flex-wrap items-center gap-3 text-sm text-stone-800">
            <span className="font-medium">Get notifications</span>
            <span className="text-stone-600">when items are added + a 7am morning briefing.</span>
            <label className="ml-auto flex items-center gap-1">
              <input type="radio" name="banner-user" value="johnny" checked={bannerUser === 'johnny'} onChange={() => setBannerUser('johnny')} />
              <span>Johnny</span>
            </label>
            <label className="flex items-center gap-1">
              <input type="radio" name="banner-user" value="elleke" checked={bannerUser === 'elleke'} onChange={() => setBannerUser('elleke')} />
              <span>Elleke</span>
            </label>
            <button onClick={onEnableBanner} className="px-3 py-1 rounded bg-emerald-700 text-white text-xs">Enable</button>
            <button onClick={onDismissBanner} className="px-3 py-1 rounded text-xs text-stone-600">Not now</button>
            {bannerError && <span className="text-rose-700 text-xs basis-full">{bannerError}</span>}
          </div>
        </div>
      )}

      <header ref={headerRef} className="border-b border-stone-200 backdrop-blur sticky top-0 z-20" style={{ backgroundColor: 'var(--theme-tint-alpha)', paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-[1600px] 2xl:max-w-[1900px] mx-auto px-4 lg:px-8 py-3 flex items-center gap-4 relative">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-900 text-white flex items-center justify-center flex-shrink-0">
              <Target size={16} />
            </div>
            <div className="min-w-0">
              <h1 className="serif text-xl lg:text-2xl leading-none tracking-tight">True North</h1>
              <p className="text-xs text-stone-500 mt-0.5 truncate">Family planning + logistics</p>
            </div>
          </div>

          <div className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <h2 className="serif text-3xl tracking-tight whitespace-nowrap" style={{ color: '#064e3b' }}>Lloyd Family</h2>
          </div>

          <nav className="hidden md:flex items-center gap-1 rounded-full p-1" style={{ backgroundColor: 'var(--theme-body)' }}>
            {['week', 'month', 'year'].map(v => (
              <button key={v}
                onClick={() => setView(v)}
                style={view === v ? { backgroundColor: 'var(--theme-accent)' } : undefined}
                className={`text-sm px-4 py-1.5 rounded-full capitalize transition ${view === v ? 'shadow-sm text-stone-900' : 'text-stone-500 hover:text-stone-900'}`}>
                {v}
              </button>
            ))}
          </nav>

          <button onClick={jumpToday} className="hidden md:block text-sm text-stone-500 hover:text-stone-900 px-3 py-1.5 rounded-full">
            Today
          </button>

          <FullscreenButton />

          <div className="relative" ref={settingsRef}>
            <button
              onClick={() => setSettingsOpen(o => !o)}
              className="p-1.5 rounded-full text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition"
              title="Settings"
            >
              <Settings size={16} />
            </button>
            {settingsOpen && (
              <div className="fixed md:absolute right-2 md:right-0 top-[calc(env(safe-area-inset-top)+3.25rem)] md:top-auto md:mt-2 w-72 max-w-[calc(100vw-1rem)] bg-white border border-stone-200 rounded-lg shadow-lg p-2 z-30">
                <div className="text-[10px] uppercase tracking-wider text-stone-400 px-2 pt-1 pb-1.5">Theme</div>
                {Object.entries(THEMES).map(([key, t]) => {
                  const isActive = state.theme === key;
                  return (
                    <button
                      key={key}
                      onClick={() => { setTheme(key); setSettingsOpen(false); }}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-stone-100 transition ${isActive ? 'text-stone-900 font-medium' : 'text-stone-600'}`}
                    >
                      <span className="w-5 h-5 rounded border border-stone-300 flex-shrink-0" style={{ backgroundColor: t.tint }} />
                      <span className="flex-1 text-left">{t.name}</span>
                      {isActive && <Check size={14} className="text-stone-700" />}
                    </button>
                  );
                })}
                <div className="border-t border-stone-200 mt-3 pt-3">
                  <div className="text-[10px] uppercase tracking-wider text-stone-400 px-2 pb-1.5">Notifications</div>

                  {!notifState.supported && (
                    <div className="text-xs text-stone-500 px-2">Not supported on this device.</div>
                  )}

                  {notifState.supported && notifState.perm === 'denied' && (
                    <div className="text-xs text-stone-600 px-2">
                      Blocked by the browser. Enable in Chrome: Settings → Site settings → Notifications → True North.
                    </div>
                  )}

                  {notifState.supported && notifState.perm !== 'denied' && (
                    <div className="text-xs space-y-2 px-2">
                      {notifState.enabled && ID.isReal(notifState.userId) ? (
                        <>
                          <div>Enabled as <strong>{notifState.userId === 'johnny' ? 'Johnny' : 'Elleke'}</strong> on this device.</div>
                          <div className="flex gap-2">
                            <button onClick={handleSendTest} disabled={notifBusy}
                              className="px-2 py-1 rounded bg-emerald-700 text-white">
                              {notifBusy ? 'Working…' : 'Send test'}
                            </button>
                            <button onClick={handleDisable} disabled={notifBusy}
                              className="px-2 py-1 rounded bg-stone-200 hover:bg-stone-300 text-stone-700">
                              Disable
                            </button>
                          </div>
                          {notifTestMsg && <div className="text-emerald-800">{notifTestMsg}</div>}
                          <div className="pt-2 text-stone-500">
                            Or switch to:
                            <button onClick={() => handleEnableAs(notifState.userId === 'johnny' ? 'elleke' : 'johnny')}
                              disabled={notifBusy}
                              className="ml-2 px-2 py-1 rounded bg-stone-200 hover:bg-stone-300 text-stone-700">
                              {notifState.userId === 'johnny' ? 'Elleke' : 'Johnny'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-stone-600">Disabled.</div>
                          <div className="flex gap-2">
                            <button onClick={() => handleEnableAs('johnny')} disabled={notifBusy}
                              className="px-2 py-1 rounded bg-emerald-700 text-white">Enable as Johnny</button>
                            <button onClick={() => handleEnableAs('elleke')} disabled={notifBusy}
                              className="px-2 py-1 rounded bg-emerald-700 text-white">Enable as Elleke</button>
                          </div>
                        </>
                      )}
                      {notifError && <div className="text-rose-700">{notifError}</div>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <SyncBadge status={syncStatus} />

          <div className="md:hidden flex items-center gap-1">
            <button onClick={() => setMobilePanel('goals')} style={mobilePanel==='goals' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><Target size={18}/></button>
            <button onClick={closeDayPanel} style={mobilePanel==='cal' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><CalIcon size={18}/></button>
            <button onClick={() => setMobilePanel('today')} style={mobilePanel==='today' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><Check size={18}/></button>
            <button onClick={() => setMobilePanel('chat')} style={mobilePanel==='chat' ? { backgroundColor: 'var(--theme-accent)' } : undefined} className="p-2 rounded"><MessageSquare size={18}/></button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] 2xl:max-w-[1900px] mx-auto px-4 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr_380px] gap-y-6 gap-x-14">

          <aside className={`space-y-5 ${mobilePanel==='goals'?'':'hidden'} md:block`}>
            <GoalsList
              goals={Array.isArray(state.goals) ? state.goals : []}
              updateGoals={updateGoalsList}
              clearAll={clearGoals}
            />

            {(state.noteGroups || []).map(group => (
              <NoteGroup
                key={group.id}
                group={group}
                setTitle={(title) => setNoteGroupTitle(group.id, title)}
                updateNotes={(fn) => updateNotesInGroup(group.id, fn)}
                clearAll={() => clearNotesInGroup(group.id)}
                onDelete={() => deleteNoteGroup(group.id)}
              />
            ))}

            <button
              onClick={addNoteGroup}
              className="text-xs text-stone-500 theme-hover transition flex items-center gap-1.5"
            >
              <Plus size={12} /> Add a list
            </button>
          </aside>

          <main className={`${mobilePanel==='cal'?'':'hidden'} md:block`}>
            {isMobile && view === 'week' ? (
              <div
                ref={weekBarRef}
                className="sticky z-10 -mx-4 px-4 h-12 mb-2 flex items-center justify-between"
                style={{ top: 'var(--hdr-h, 64px)', backgroundColor: 'var(--theme-body)' }}
              >
                <TopMonthLabel barRef={weekBarRef} fallback={anchor} />
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setWeekScrollReq({ key: fmtDate(addDays(today, -1)), n: Date.now() })}
                    className="text-sm text-stone-600 bg-stone-100 rounded-full px-3 py-1.5"
                  >
                    Today
                  </button>
                  <select value={view} onChange={e=>setView(e.target.value)} className="text-sm bg-stone-100 rounded-full px-3 py-1.5 border-0 capitalize">
                    <option value="week">week</option>
                    <option value="month">month</option>
                    <option value="year">year</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <button onClick={navigatePrev} className="p-1.5 hover:bg-stone-100 rounded"><ChevronLeft size={18} /></button>
                  <h2 className="serif text-2xl tracking-tight">{viewLabel}</h2>
                  <button onClick={navigateNext} className="p-1.5 hover:bg-stone-100 rounded"><ChevronRight size={18} /></button>
                </div>
                <div className="md:hidden">
                  <select value={view} onChange={e=>setView(e.target.value)} className="text-sm bg-stone-100 rounded-full px-3 py-1.5 border-0 capitalize">
                    <option value="week">week</option>
                    <option value="month">month</option>
                    <option value="year">year</option>
                  </select>
                </div>
              </div>
            )}

            {view === 'week' && <WeekView anchor={anchor} today={today} state={state} selectedDay={selectedDay} onSelectDay={selectDay} moveItem={moveItem} enableDrag={enableDrag} infinite={isMobile} active={mobilePanel === 'cal'} scrollReq={weekScrollReq} />}
            {view === 'month' && <MonthView anchor={anchor} today={today} state={state} selectedDay={selectedDay} onSelectDay={selectDay} setAnchor={setAnchor} setView={setView} />}
            {view === 'year' && <YearView anchor={anchor} today={today} state={state} setAnchor={setAnchor} setView={setView} />}
          </main>

          <aside className={`space-y-4 ${['today','chat'].includes(mobilePanel)?'':'hidden'} md:block`}>
            <div className={mobilePanel==='chat' ? 'hidden md:block' : ''}>
              <TodayPanel
                dayKey={selectedDay}
                day={state.days[selectedDay]}
                isToday={selectedDay === todayKey}
                addItem={addItem}
                deleteItem={deleteItem}
                editItem={editItem}
                reorderItems={reorderItems}
                enableDrag={enableDrag}
                setNotes={setNotes}
                setSelectedDay={setSelectedDay}
                setAnchor={setAnchor}
                today={today}
                onSwipeDay={(delta) => setSelectedDay(k => fmtDate(addDays(parseDate(k), delta)))}
              />
            </div>

            <div className={mobilePanel==='today' ? 'hidden md:block' : ''}>
              <ChatPanel
                dayKey={selectedDay}
                messages={Array.isArray(state.chats) ? state.chats : []}
                input={chatInput}
                setInput={setChatInput}
                send={sendMessage}
                loading={chatLoading}
                error={chatError}
                onAddItem={(text, dates) => {
                  const keys = dates && dates.length ? dates : [selectedDay];
                  keys.forEach(k => addItem(k, text));
                }}
                onScan={scanCalendar}
                scanLoading={scanLoading}
                scanError={scanError}
                onClear={clearChats}
                chatEndRef={chatEndRef}
                mobileOnly={mobilePanel==='chat'}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function FullscreenButton() {
  const [isFull, setIsFull] = useState(false);
  useEffect(() => {
    const handler = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);
  const toggle = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else document.documentElement.requestFullscreen?.();
  };
  const Icon = isFull ? Minimize2 : Maximize2;
  return (
    <button
      onClick={toggle}
      title={isFull ? 'Exit fullscreen' : 'Fullscreen'}
      className="p-1.5 text-stone-400 hover:text-stone-900 rounded"
    >
      <Icon size={16} />
    </button>
  );
}

function SyncBadge({ status }) {
  const config = {
    loading: { dot: 'bg-stone-300 animate-pulse', label: 'Loading' },
    saving:  { dot: 'bg-amber-400 animate-pulse',  label: 'Saving' },
    saved:   { dot: 'bg-emerald-600',              label: 'Synced' },
    error:   { dot: 'bg-red-500',                  label: 'Sync error' },
    offline: { dot: 'bg-stone-400',                label: 'Offline' },
  }[status] || { dot: 'bg-stone-300', label: '' };
  return (
    <div className="hidden md:flex items-center gap-1.5 text-xs text-stone-500" title={config.label}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      <span>{config.label}</span>
    </div>
  );
}

// Month/year of whatever day sits just under the sticky week bar (mobile infinite list).
function TopMonthLabel({ barRef, fallback }) {
  const fmt = (d) => `${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}`;
  const [label, setLabel] = useState(() => fmt(fallback));
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const bar = barRef.current;
      if (!bar) return;
      const y = bar.getBoundingClientRect().bottom + 12;
      const hit = document.elementFromPoint(window.innerWidth / 2, y)?.closest?.('[data-day-key], [data-week-start]');
      const key = hit?.dataset?.dayKey || hit?.dataset?.weekStart;
      if (!key) return;
      const next = fmt(parseDate(key));
      setLabel(prev => (prev === next ? prev : next));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [barRef]);
  return <h2 className="serif text-2xl tracking-tight">{label}</h2>;
}

const WEEK_LOAD_CHUNK = 56; // days added per infinite-scroll load (8 weeks)
const weekRangeAround = (d) => ({ start: addDays(startOfWeek(d), -28), count: 28 + 7 * 8 });

function WeekView({ anchor, today, state, selectedDay, onSelectDay, moveItem, enableDrag, infinite = false, active = true, scrollReq = null }) {
  // Desktop: fixed 14-day grid starting yesterday. Phone (infinite): Monday-aligned range that grows both ways.
  const [range, setRange] = useState(() => weekRangeAround(addDays(anchor, -1)));
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const days = infinite
    ? Array.from({ length: range.count }, (_, i) => addDays(range.start, i))
    : Array.from({ length: 14 }, (_, i) => addDays(anchor, i - 1));
  const listRef = useRef(null);
  const topSentinelRef = useRef(null);
  const bottomSentinelRef = useRef(null);
  const prependRef = useRef(null);        // { key, top } — keeps the viewport still when weeks are added above
  const pendingScrollRef = useRef(null);  // { key, block } — scroll once a reset range has rendered
  const activeRef = useRef(active);
  const wasActiveRef = useRef(active);
  const savedRef = useRef({ y: null, sel: selectedDay });
  const anchorKeyRef = useRef(fmtDate(anchor));

  const cardEl = (key) => listRef.current?.querySelector(`[data-day-key="${key}"]`);
  const scrollToKey = (key, { block = 'start', behavior = 'auto' } = {}) => {
    const d = parseDate(key);
    const r = rangeRef.current;
    const el = d >= r.start && d < addDays(r.start, r.count) ? cardEl(key) : null;
    if (el) { el.scrollIntoView({ block, behavior }); return; }
    pendingScrollRef.current = { key, block };
    setRange(weekRangeAround(d));
  };

  // Browser scroll anchoring (Chrome) would fight the manual prepend compensation below.
  useEffect(() => {
    if (!infinite) return;
    const html = document.documentElement, body = document.body;
    const prev = [html.style.overflowAnchor, body.style.overflowAnchor];
    html.style.overflowAnchor = 'none';
    body.style.overflowAnchor = 'none';
    return () => { html.style.overflowAnchor = prev[0]; body.style.overflowAnchor = prev[1]; };
  }, [infinite]);

  // After the range changes: hold position on prepend, or perform a pending jump.
  useLayoutEffect(() => {
    if (!infinite) return;
    const p = prependRef.current;
    if (p) {
      prependRef.current = null;
      const el = cardEl(p.key);
      if (el) window.scrollBy(0, el.getBoundingClientRect().top - p.top);
    }
    const s = pendingScrollRef.current;
    if (s) {
      pendingScrollRef.current = null;
      cardEl(s.key)?.scrollIntoView({ block: s.block });
    }
  }, [range, infinite]);

  // Initial position: yesterday at the top (same as the desktop grid).
  useLayoutEffect(() => {
    if (!infinite || !activeRef.current) return;
    scrollToKey(fmtDate(addDays(anchor, -1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infinite]);

  // Leaving/returning to the calendar tab: restore scroll, or centre a day that changed meanwhile (e.g. swiped to).
  useLayoutEffect(() => {
    activeRef.current = active;
    if (infinite && active && !wasActiveRef.current) {
      const { y, sel } = savedRef.current;
      if (sel === selectedDay && y != null) window.scrollTo(0, y);
      else scrollToKey(selectedDay, { block: 'center' });
    }
    if (!active && wasActiveRef.current) savedRef.current.sel = selectedDay;
    wasActiveRef.current = active;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, infinite]);

  useEffect(() => {
    if (!infinite) return;
    const onScroll = () => { if (activeRef.current) savedRef.current.y = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [infinite]);

  // Anchor moved elsewhere (deep link, date picker) while the list is visible → jump there.
  useEffect(() => {
    const k = fmtDate(anchor);
    if (k === anchorKeyRef.current) return;
    anchorKeyRef.current = k;
    if (infinite && activeRef.current) scrollToKey(fmtDate(addDays(anchor, -1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, infinite]);

  useEffect(() => {
    if (!infinite || !scrollReq) return;
    scrollToKey(scrollReq.key, { behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollReq, infinite]);

  // Load more weeks when a sentinel gets within ~3000px of the viewport.
  useEffect(() => {
    if (!infinite) return;
    const top = topSentinelRef.current, bottom = bottomSentinelRef.current;
    if (!top || !bottom) return;
    const io = new IntersectionObserver((entries) => {
      if (!activeRef.current) return;
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        if (e.target === bottom) {
          setRange(r => ({ ...r, count: r.count + WEEK_LOAD_CHUNK }));
        } else if (e.target === top && !prependRef.current) {
          const firstKey = fmtDate(rangeRef.current.start);
          const el = cardEl(firstKey);
          if (!el) continue;
          prependRef.current = { key: firstKey, top: el.getBoundingClientRect().top };
          setRange(r => ({ start: addDays(r.start, -WEEK_LOAD_CHUNK), count: r.count + WEEK_LOAD_CHUNK }));
        }
      }
    }, { rootMargin: '3000px 0px' });
    io.observe(top);
    io.observe(bottom);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infinite]);

  const [dragOverKey, setDragOverKey] = useState(null);
  const [touchDragId, setTouchDragId] = useState(null);
  const dragOverKeyRef = useRef(null);
  const touchDragRef = useRef(null);
  const longPressRef = useRef(null);
  const touchStartRef = useRef(null);
  const movedRef = useRef(false);
  const justActivatedRef = useRef(false);

  useEffect(() => { dragOverKeyRef.current = dragOverKey; }, [dragOverKey]);

  useEffect(() => {
    if (!touchDragId) return;
    const onMove = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const dayEl = el?.closest?.('[data-day-key]');
      const k = dayEl?.dataset?.dayKey || null;
      if (k !== dragOverKeyRef.current) setDragOverKey(k);
    };
    const onEnd = () => {
      const overKey = dragOverKeyRef.current;
      const drag = touchDragRef.current;
      if (drag && overKey && overKey !== drag.fromKey) {
        moveItem(drag.fromKey, overKey, drag.id);
      }
      touchDragRef.current = null;
      setDragOverKey(null);
      setTouchDragId(null);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [touchDragId, moveItem]);

  const cancelLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  return (
    <div ref={listRef} className="grid grid-cols-1 md:grid-cols-7 gap-2">
      {infinite && <div ref={topSentinelRef} aria-hidden="true" className="h-px" />}
      {days.map((d) => {
        const key = fmtDate(d);
        const day = state.days[key] || { items: [] };
        const isToday = sameDay(d, today);
        const isSelected = key === selectedDay;
        const dayName = DAYS_SHORT[(d.getDay() + 6) % 7];
        const isDropTarget = (enableDrag || touchDragId) && dragOverKey === key;
        const card = (
          <button
            key={key}
            data-day-key={key}
            onClick={() => {
              if (infinite) savedRef.current.y = window.scrollY;
              onSelectDay(key);
            }}
            onDragOver={enableDrag ? (e) => {
              if (!e.dataTransfer.types.includes('application/x-family-item')) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (dragOverKey !== key) setDragOverKey(key);
            } : undefined}
            onDragLeave={enableDrag ? (e) => {
              if (e.currentTarget.contains(e.relatedTarget)) return;
              if (dragOverKey === key) setDragOverKey(null);
            } : undefined}
            onDrop={enableDrag ? (e) => {
              e.preventDefault();
              setDragOverKey(null);
              try {
                const raw = e.dataTransfer.getData('application/x-family-item');
                if (!raw) return;
                const data = JSON.parse(raw);
                if (data && data.fromKey != null && data.id != null) moveItem(data.fromKey, key, data.id);
              } catch {}
            } : undefined}
            style={{
              ...(isSelected ? { backgroundColor: 'var(--theme-tint)' } : {}),
              ...(infinite ? { scrollMarginTop: 'calc(var(--hdr-h, 64px) + 56px)' } : {}),
            }}
            className={`text-left p-3 rounded-lg border transition h-[210px] flex flex-col ${
              isDropTarget ? 'border-emerald-700 ring-2 ring-emerald-600/40 bg-white' :
              isSelected ? 'border-emerald-900 shadow-sm' :
              isToday ? 'border-stone-300 bg-white' :
              'border-stone-200 hover:bg-white'
            }`}
          >
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-stone-400">{dayName}</div>
                <div className={`serif text-xl leading-none ${isToday ? 'text-emerald-900' : ''}`}>{d.getDate()}</div>
              </div>
              {day.items.length > 0 && (
                <div className="text-[10px] text-stone-400 tabular-nums">{day.items.length}</div>
              )}
            </div>
            <ul className="space-y-1 flex-1 overflow-hidden">
              {day.items.slice(0, 4).map(item => (
                <li
                  key={item.id}
                  draggable={enableDrag}
                  style={{ WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
                  onDragStart={enableDrag ? (e) => {
                    e.stopPropagation();
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('application/x-family-item', JSON.stringify({ fromKey: key, id: item.id }));
                  } : undefined}
                  onDragEnd={enableDrag ? () => { setDragOverKey(null); } : undefined}
                  onTouchStart={(e) => {
                    if (touchDragRef.current) return;
                    const t = e.touches[0];
                    touchStartRef.current = { x: t.clientX, y: t.clientY };
                    movedRef.current = false;
                    justActivatedRef.current = false;
                    cancelLongPress();
                    const fromKey = key;
                    const id = item.id;
                    longPressRef.current = setTimeout(() => {
                      longPressRef.current = null;
                      if (movedRef.current) return;
                      if (navigator.vibrate) navigator.vibrate(10);
                      justActivatedRef.current = true;
                      touchDragRef.current = { id, fromKey };
                      setTouchDragId(id);
                    }, 400);
                  }}
                  onTouchMove={(e) => {
                    if (touchDragRef.current) return;
                    const t = e.touches[0];
                    if (!t || !touchStartRef.current) return;
                    const dx = Math.abs(t.clientX - touchStartRef.current.x);
                    const dy = Math.abs(t.clientY - touchStartRef.current.y);
                    if (dx > 8 || dy > 8) {
                      movedRef.current = true;
                      cancelLongPress();
                    }
                  }}
                  onTouchEnd={cancelLongPress}
                  onTouchCancel={cancelLongPress}
                  onClick={(e) => {
                    if (justActivatedRef.current) {
                      e.stopPropagation();
                      justActivatedRef.current = false;
                    }
                  }}
                  className={`select-none text-xs leading-snug flex gap-1.5 text-stone-700 ${enableDrag ? 'cursor-grab active:cursor-grabbing' : ''} ${touchDragId === item.id ? 'font-medium scale-[1.03]' : ''} transition-transform`}
                >
                  <span className="flex-shrink-0 text-stone-300 leading-snug">•</span>
                  <span className="truncate">{item.text}</span>
                </li>
              ))}
              {day.items.length > 4 && (
                <li className="text-[10px] text-stone-400">+{day.items.length - 4} more</li>
              )}
            </ul>
          </button>
        );
        if (infinite && d.getDay() === 1) {
          const end = addDays(d, 6);
          const span = d.getMonth() === end.getMonth()
            ? `${MONTHS[d.getMonth()]} ${d.getDate()} – ${end.getDate()}`
            : `${MONTHS[d.getMonth()]} ${d.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}`;
          return (
            <React.Fragment key={key}>
              <div data-week-start={key} className="pt-3 px-1 text-[10px] uppercase tracking-wider text-stone-400">
                Week {weekNum(d)} · {span}
              </div>
              {card}
            </React.Fragment>
          );
        }
        return card;
      })}
      {infinite && <div ref={bottomSentinelRef} aria-hidden="true" className="h-px" />}
    </div>
  );
}

function MonthView({ anchor, today, state, selectedDay, onSelectDay, setAnchor, setView }) {
  const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(firstOfMonth);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_SHORT.map(d => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-stone-400 text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map(d => {
          const key = fmtDate(d);
          const day = state.days[key];
          const inMonth = d.getMonth() === anchor.getMonth();
          const isToday = sameDay(d, today);
          const isSelected = key === selectedDay;
          const hasItems = day?.items?.length > 0;
          const total = day?.items?.length || 0;
          return (
            <button
              key={key}
              onClick={() => onSelectDay(key)}
              onDoubleClick={() => { setAnchor(d); onSelectDay(key); setView('week'); }}
              style={isSelected ? { backgroundColor: 'var(--theme-tint)' } : undefined}
              className={`aspect-square p-2 text-left rounded-lg border transition flex flex-col ${
                isSelected ? 'border-emerald-900 shadow-sm' :
                isToday ? 'border-stone-300 bg-white' :
                'border-transparent hover:border-stone-200'
              } ${!inMonth ? 'opacity-30' : ''}`}
            >
              <div className={`serif text-base leading-none ${isToday ? 'text-emerald-900' : 'text-stone-700'}`}>{d.getDate()}</div>
              {hasItems && (
                <div className="mt-auto flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-800 flex-shrink-0" />
                  <span className="text-[9px] text-stone-400 tabular-nums">{total}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function YearView({ anchor, today, state, setAnchor, setView }) {
  const year = anchor.getFullYear();
  const quarters = [1, 2, 3, 4];
  return (
    <div className="space-y-4">
      {quarters.map(q => {
        const qKey = `${year}-Q${q}`;
        const qData = (state.quarters || {})[qKey];
        const qStartMonth = (q - 1) * 3;
        const isCurrent = today.getFullYear() === year && Math.floor(today.getMonth() / 3) + 1 === q;
        return (
          <div key={qKey} className={`border rounded-xl p-4 ${isCurrent ? 'border-emerald-900 bg-white' : 'border-stone-200'}`}>
            <div className="flex items-baseline justify-between mb-3">
              <div className="flex items-baseline gap-3">
                <h3 className="serif text-xl tracking-tight">Q{q}</h3>
                <span className="text-xs text-stone-500">{MONTHS[qStartMonth]} – {MONTHS[qStartMonth + 2]} {year}</span>
              </div>
              {isCurrent && <span className="text-[10px] uppercase tracking-wider text-emerald-900 font-medium">Current</span>}
            </div>
            {qData?.focus && <p className="text-sm text-stone-700 mb-3 leading-relaxed">{qData.focus}</p>}
            {qData?.milestones?.length > 0 && (
              <ul className="space-y-1">
                {qData.milestones.map((m, i) => (
                  <li key={i} className="text-xs text-stone-600 flex gap-2">
                    <span className="text-stone-300">◇</span><span>{m}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-stone-100">
              {[0, 1, 2].map(offset => {
                const m = qStartMonth + offset;
                const monthDate = new Date(year, m, 1);
                return (
                  <button
                    key={m}
                    onClick={() => { setAnchor(monthDate); setView('month'); }}
                    className="text-xs text-stone-500 hover:text-stone-900 hover:bg-stone-50 rounded py-1.5 transition"
                  >
                    {MONTHS[m]}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TodayPanel({ dayKey, day, isToday, addItem, deleteItem, editItem, reorderItems, enableDrag, setNotes, setSelectedDay, setAnchor, today, onSwipeDay }) {
  const [newItem, setNewItem] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => parseDate(dayKey));
  const pickerRef = useRef(null);
  const d = parseDate(dayKey);
  const items = day?.items || [];

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const editInputRef = useRef(null);
  useEffect(() => {
    if (editingId != null) editInputRef.current?.focus();
  }, [editingId]);
  useEffect(() => { setEditingId(null); }, [dayKey]);
  const startEdit = (item) => { setEditingId(item.id); setEditText(item.text); };
  const commitEdit = (id) => {
    if (editText.trim()) editItem && editItem(dayKey, id, editText);
    setEditingId(null); setEditText('');
  };
  const cancelEdit = () => { setEditingId(null); setEditText(''); };

  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [touchDragIdx, setTouchDragIdx] = useState(null);
  const touchDragRef = useRef(null);
  const dragOverIdxRef = useRef(null);
  const longPressRef = useRef(null);
  const touchStartRef = useRef(null);
  const movedRef = useRef(false);
  const justActivatedRef = useRef(false);

  useEffect(() => { dragOverIdxRef.current = dragOverIdx; }, [dragOverIdx]);

  const cancelLongPress = () => {
    if (longPressRef.current) { clearTimeout(longPressRef.current); longPressRef.current = null; }
  };

  useEffect(() => {
    if (touchDragIdx == null) return;
    const onMove = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      if (!t) return;
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const li = el?.closest?.('[data-item-idx]');
      const raw = li?.dataset?.itemIdx;
      const idx = raw != null ? parseInt(raw, 10) : null;
      if (idx !== dragOverIdxRef.current) setDragOverIdx(idx);
    };
    const onEnd = () => {
      const drag = touchDragRef.current;
      const overIdx = dragOverIdxRef.current;
      if (drag != null && overIdx != null && overIdx !== drag.from) {
        reorderItems && reorderItems(dayKey, drag.from, overIdx);
      }
      touchDragRef.current = null;
      setDragOverIdx(null);
      setTouchDragIdx(null);
    };
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [touchDragIdx, dayKey, reorderItems]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setPickerOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  const openPicker = () => {
    setPickerMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    setPickerOpen(o => !o);
  };
  const pickDate = (date) => {
    const k = fmtDate(date);
    setSelectedDay && setSelectedDay(k);
    setAnchor && setAnchor(date);
    setPickerOpen(false);
  };

  // Horizontal swipe → previous / next day (touch only).
  const [swipeX, setSwipeX] = useState(0);
  const [swipeSnap, setSwipeSnap] = useState(false);
  const [swipeN, setSwipeN] = useState(0);       // bumps on each committed swipe to replay the slide-in
  const [enterDir, setEnterDir] = useState(null); // 'left' | 'right'
  const swipeRef = useRef(null);
  const onSwipeStart = (e) => {
    swipeRef.current = null;
    if (!onSwipeDay || e.touches.length !== 1) return;
    if (touchDragRef.current || pickerOpen || editingId != null) return;
    if (e.target.closest('input, textarea, select')) return;
    const t = e.touches[0];
    if (t.clientX < 20 || t.clientX > window.innerWidth - 20) return; // leave screen edges to the OS back gesture
    swipeRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), axis: null, dx: 0 };
  };
  const onSwipeMove = (e) => {
    const s = swipeRef.current;
    if (!s) return;
    if (touchDragRef.current) { swipeRef.current = null; setSwipeX(0); return; }
    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (!s.axis) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      s.axis = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
    }
    if (s.axis !== 'x') return;
    s.dx = dx;
    setSwipeSnap(false);
    setSwipeX(dx);
  };
  const onSwipeEnd = () => {
    const s = swipeRef.current;
    swipeRef.current = null;
    if (!s || s.axis !== 'x') return;
    const dist = Math.abs(s.dx);
    const velocity = dist / Math.max(1, Date.now() - s.t);
    if (dist > 70 || (dist > 30 && velocity > 0.4)) {
      const delta = s.dx < 0 ? 1 : -1; // swipe left → next day
      setSwipeSnap(false);
      setSwipeX(0);
      setEnterDir(delta > 0 ? 'right' : 'left');
      setSwipeN(n => n + 1);
      onSwipeDay(delta);
    } else {
      setSwipeSnap(true);
      setSwipeX(0);
    }
  };

  return (
    <div
      onTouchStart={onSwipeStart}
      onTouchMove={onSwipeMove}
      onTouchEnd={onSwipeEnd}
      onTouchCancel={onSwipeEnd}
      className={onSwipeDay ? 'min-h-[70vh] md:min-h-0' : ''}
      style={onSwipeDay ? { touchAction: 'pan-y' } : undefined}
    >
    <div
      key={swipeN}
      className={`bg-white border border-stone-200 rounded-xl p-4 ${enterDir ? `slide-from-${enterDir}` : 'fade-in'}`}
      style={{
        transform: swipeX ? `translateX(${swipeX}px)` : undefined,
        opacity: swipeX ? Math.max(0.5, 1 - Math.abs(swipeX) / 500) : undefined,
        transition: swipeSnap ? 'transform 0.2s ease, opacity 0.2s ease' : undefined,
      }}
    >
      <div className="flex items-baseline justify-between mb-3 relative">
        <button
          type="button"
          onClick={openPicker}
          aria-label="Pick a date"
          aria-expanded={pickerOpen}
          className="text-left group -m-1 p-1 rounded hover:bg-stone-50 transition"
        >
          <div className="text-[10px] uppercase tracking-wider text-stone-400">
            {isToday ? 'Today' : DAYS_SHORT[(d.getDay() + 6) % 7]}
          </div>
          <div className="serif text-lg tracking-tight flex items-center gap-1 group-hover:[color:var(--theme-strong)] transition">
            {MONTHS[d.getMonth()]} {d.getDate()}
            <ChevronDown size={14} className="text-stone-400 group-hover:[color:var(--theme-strong)] transition" />
          </div>
        </button>
        {items.length > 0 && (
          <div className="text-xs text-stone-500 tabular-nums">{items.length}</div>
        )}
        {pickerOpen && (
          <div ref={pickerRef} className="absolute left-0 top-full mt-2 z-30">
            <DatePickerPopover
              selected={d}
              today={today}
              month={pickerMonth}
              setMonth={setPickerMonth}
              onPick={pickDate}
            />
          </div>
        )}
      </div>

      <ul className="space-y-1.5 mb-3">
        {items.length === 0 && <li className="text-xs text-stone-400 italic">No items yet.</li>}
        {items.map((item, idx) => {
          const isEditing = editingId === item.id;
          const isDragging = draggedIdx === idx || touchDragIdx === idx;
          const showDropLine = dragOverIdx === idx && (draggedIdx != null || touchDragIdx != null) && (draggedIdx ?? touchDragIdx) !== idx;
          return (
            <li
              key={item.id}
              data-item-idx={idx}
              draggable={enableDrag && !isEditing}
              onDragStart={enableDrag ? (e) => {
                if (isEditing) return;
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('application/x-family-todaypanel-idx', String(idx));
                setDraggedIdx(idx);
              } : undefined}
              onDragOver={enableDrag ? (e) => {
                if (draggedIdx == null) return;
                if (!e.dataTransfer.types.includes('application/x-family-todaypanel-idx')) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverIdx !== idx) setDragOverIdx(idx);
              } : undefined}
              onDragLeave={enableDrag ? (e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                if (dragOverIdx === idx) setDragOverIdx(null);
              } : undefined}
              onDrop={enableDrag ? (e) => {
                e.preventDefault();
                const raw = e.dataTransfer.getData('application/x-family-todaypanel-idx');
                const from = raw === '' ? null : parseInt(raw, 10);
                if (from != null && !Number.isNaN(from) && from !== idx) {
                  reorderItems && reorderItems(dayKey, from, idx);
                }
                setDraggedIdx(null);
                setDragOverIdx(null);
              } : undefined}
              onDragEnd={enableDrag ? () => { setDraggedIdx(null); setDragOverIdx(null); } : undefined}
              onTouchStart={(e) => {
                if (isEditing || enableDrag) return;
                if (touchDragRef.current) return;
                if (e.target.closest('button, input, textarea, a')) return;
                const t = e.touches[0];
                touchStartRef.current = { x: t.clientX, y: t.clientY };
                movedRef.current = false;
                justActivatedRef.current = false;
                cancelLongPress();
                longPressRef.current = setTimeout(() => {
                  longPressRef.current = null;
                  if (movedRef.current) return;
                  if (navigator.vibrate) navigator.vibrate(10);
                  justActivatedRef.current = true;
                  touchDragRef.current = { from: idx };
                  setTouchDragIdx(idx);
                }, 400);
              }}
              onTouchMove={(e) => {
                if (touchDragRef.current) return;
                const t = e.touches[0];
                if (!t || !touchStartRef.current) return;
                const dx = Math.abs(t.clientX - touchStartRef.current.x);
                const dy = Math.abs(t.clientY - touchStartRef.current.y);
                if (dx > 8 || dy > 8) { movedRef.current = true; cancelLongPress(); }
              }}
              onTouchEnd={cancelLongPress}
              onTouchCancel={cancelLongPress}
              className={`group flex items-start gap-2 text-sm relative ${isDragging ? 'opacity-60' : ''} ${touchDragIdx === idx ? 'scale-[1.02] font-medium' : ''} transition-transform`}
              style={{ WebkitTouchCallout: 'none', WebkitUserSelect: isEditing ? 'auto' : 'none' }}
            >
              {showDropLine && (
                <span className="absolute -top-[3px] left-0 right-0 h-0.5 rounded pointer-events-none" style={{ backgroundColor: 'var(--theme-accent)' }} />
              )}
              {enableDrag && (
                <span
                  className="flex-shrink-0 mt-0.5 text-stone-300 opacity-0 group-hover:opacity-100 transition cursor-grab active:cursor-grabbing"
                  aria-label="Drag to reorder"
                >
                  <GripVertical size={14} />
                </span>
              )}
              <span className="flex-shrink-0 text-stone-300 mt-0.5 leading-snug">•</span>
              {isEditing ? (
                <input
                  ref={editInputRef}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitEdit(item.id); }
                    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                  }}
                  onBlur={() => commitEdit(item.id)}
                  className="flex-1 leading-snug text-sm bg-stone-50 border border-stone-300 rounded px-1.5 py-0.5 focus:outline-none focus:border-stone-500"
                />
              ) : (
                <span
                  onClick={() => {
                    if (justActivatedRef.current) { justActivatedRef.current = false; return; }
                    startEdit(item);
                  }}
                  className="flex-1 leading-snug text-stone-800 cursor-text"
                >
                  {item.text}
                </span>
              )}
              {!isEditing && (
                <button
                  onClick={() => deleteItem(dayKey, item.id)}
                  className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-stone-600 transition"
                >
                  <X size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex gap-2">
        <input
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { addItem(dayKey, newItem); setNewItem(''); } }}
          placeholder="Add event…"
          className="flex-1 text-sm px-3 py-1.5 bg-stone-50 border border-stone-200 rounded focus:outline-none focus:border-stone-400"
        />
        <button
          onClick={() => { addItem(dayKey, newItem); setNewItem(''); }}
          className="px-3 bg-stone-900 text-white rounded hover:bg-stone-700 transition"
        >
          <Plus size={14} />
        </button>
      </div>

      <textarea
        value={day?.notes || ''}
        onChange={e => setNotes(dayKey, e.target.value)}
        placeholder="Notes, blockers, wins…"
        className="mt-3 w-full text-xs p-2 bg-stone-50 border border-stone-200 rounded resize-none focus:outline-none focus:border-stone-400"
        rows={2}
      />
    </div>
    </div>
  );
}

function DatePickerPopover({ selected, today, month, setMonth, onPick }) {
  const firstOfMonth = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = startOfWeek(firstOfMonth);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const selKey = fmtDate(selected);
  const todayKey = fmtDate(today);
  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-lg p-3 w-[260px]">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setMonth(addMonths(month, -1))}
          className="p-1 rounded hover:bg-stone-100 text-stone-500"
          aria-label="Previous month"
        >
          <ChevronLeft size={14} />
        </button>
        <div className="serif text-sm tracking-tight">
          {MONTHS_FULL[month.getMonth()]} {month.getFullYear()}
        </div>
        <button
          type="button"
          onClick={() => setMonth(addMonths(month, 1))}
          className="p-1 rounded hover:bg-stone-100 text-stone-500"
          aria-label="Next month"
        >
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAYS_SHORT.map(dn => (
          <div key={dn} className="text-[9px] uppercase tracking-wider text-stone-400 text-center py-1">{dn[0]}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {cells.map(d => {
          const k = fmtDate(d);
          const inMonth = d.getMonth() === month.getMonth();
          const isToday = k === todayKey;
          const isSelected = k === selKey;
          return (
            <button
              key={k}
              type="button"
              onClick={() => onPick(d)}
              className={`aspect-square text-xs rounded transition flex items-center justify-center tabular-nums ${
                isSelected ? 'bg-emerald-900 text-white' :
                isToday ? 'bg-stone-100 text-emerald-900 font-medium' :
                'text-stone-700 hover:bg-stone-100'
              } ${!inMonth ? 'opacity-30' : ''}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function autoResize(el) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
}

function GoalsList({ goals, updateGoals, clearAll }) {
  const [editing, setEditing] = useState({ id: null, text: '' });
  const inputRef = useRef(null);
  const skipBlur = useRef(false);

  useEffect(() => {
    if (editing.id && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [editing.id]);

  const startEdit = (g) => setEditing({ id: g.id, text: g.text });

  const startNewGoal = () => {
    const newId = Date.now() + Math.random();
    updateGoals(gs => [...gs, { id: newId, text: '', addedAt: Date.now() }]);
    setEditing({ id: newId, text: '' });
  };

  const commit = () => {
    if (skipBlur.current) { skipBlur.current = false; return; }
    setEditing(prev => {
      if (!prev.id) return prev;
      const trimmed = prev.text.trim();
      if (trimmed === '') {
        updateGoals(gs => gs.filter(g => g.id !== prev.id));
      } else {
        updateGoals(gs => gs.map(g => g.id === prev.id ? { ...g, text: trimmed } : g));
      }
      return { id: null, text: '' };
    });
  };

  const cancel = () => {
    skipBlur.current = true;
    setEditing(prev => {
      if (prev.id && prev.text.trim() === '') {
        updateGoals(gs => gs.filter(g => g.id !== prev.id));
      }
      return { id: null, text: '' };
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = editing.text.trim();
      if (trimmed === '') {
        const curId = editing.id;
        skipBlur.current = true;
        updateGoals(gs => gs.filter(g => g.id !== curId));
        setEditing({ id: null, text: '' });
        return;
      }
      const curId = editing.id;
      const newId = Date.now() + Math.random();
      skipBlur.current = true;
      updateGoals(gs => {
        const i = gs.findIndex(g => g.id === curId);
        if (i < 0) return gs;
        const out = [...gs];
        out[i] = { ...out[i], text: trimmed };
        out.splice(i + 1, 0, { id: newId, text: '', addedAt: Date.now() });
        return out;
      });
      setEditing({ id: newId, text: '' });
    }
  };

  const removeGoal = (id) => {
    if (editing.id === id) { skipBlur.current = true; setEditing({ id: null, text: '' }); }
    updateGoals(gs => gs.filter(g => g.id !== id));
  };

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 flex flex-col space-y-2 fade-in">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium" style={{ color: 'var(--theme-strong)' }}>
        <Target size={14} />Goals
      </div>
      <ul className="space-y-1.5">
        {goals.map((g) => (
          <li key={g.id} className="group flex items-start gap-2 text-stone-700">
            <span className="text-stone-300 mt-0.5 flex-shrink-0 leading-snug">•</span>
            {editing.id === g.id ? (
              <input
                ref={inputRef}
                value={editing.text}
                onChange={e => setEditing(prev => ({ ...prev, text: e.target.value }))}
                onKeyDown={handleKeyDown}
                onBlur={commit}
                data-tn="goal"
                className="flex-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5 focus:outline-none focus:border-stone-400 leading-snug"
              />
            ) : (
              <span
                data-tn="goal"
                className="flex-1 leading-snug whitespace-pre-wrap break-words cursor-text"
                onClick={() => startEdit(g)}
              >
                {g.text}
              </span>
            )}
            <button
              onClick={() => removeGoal(g.id)}
              className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-stone-600 transition flex-shrink-0 mt-1.5"
              aria-label="Remove goal"
              tabIndex={-1}
            >
              <X size={12} />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={startNewGoal}
        data-tn="goal"
        className="text-stone-400 italic hover:text-stone-600 transition text-left flex items-center gap-2"
      >
        <span className="text-stone-300 flex-shrink-0">•</span>
        <span>Add a goal…</span>
      </button>
      {goals.length > 0 && (
        <button
          onClick={clearAll}
          className="text-[11px] text-stone-400 hover:text-red-600 transition self-start"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

function NoteGroup({ group, setTitle, updateNotes, clearAll, onDelete }) {
  const notes = Array.isArray(group.notes) ? group.notes : [];
  const [editing, setEditing] = useState({ id: null, text: '' });
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(group.title || '');
  const textareaRef = useRef(null);
  const titleInputRef = useRef(null);
  const pendingCaret = useRef(null);
  const skipBlur = useRef(false);

  // Auto-edit the title for newly-created (untitled) groups.
  useEffect(() => {
    if (!group.title) {
      setTitleDraft('');
      setEditingTitle(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      const len = titleInputRef.current.value.length;
      titleInputRef.current.setSelectionRange(len, len);
    }
  }, [editingTitle]);

  const startEditTitle = () => {
    setTitleDraft(group.title || '');
    setEditingTitle(true);
  };
  const commitTitle = () => {
    setTitle(titleDraft.trim());
    setEditingTitle(false);
  };
  const onTitleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
    else if (e.key === 'Escape') { e.preventDefault(); setTitleDraft(group.title || ''); setEditingTitle(false); }
  };

  useEffect(() => {
    if (editing.id && textareaRef.current) {
      const el = textareaRef.current;
      autoResize(el);
      el.focus();
      const c = pendingCaret.current;
      if (typeof c === 'number') {
        el.setSelectionRange(c, c);
      } else {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
      pendingCaret.current = null;
    }
  }, [editing.id]);

  const startEdit = (n) => setEditing({ id: n.id, text: n.text });

  const startNewBullet = () => {
    const newId = Date.now() + Math.random();
    updateNotes(ns => [...ns, { id: newId, text: '', addedAt: Date.now() }]);
    setEditing({ id: newId, text: '' });
  };

  const commit = () => {
    if (skipBlur.current) { skipBlur.current = false; return; }
    setEditing(prev => {
      if (!prev.id) return prev;
      if (prev.text === '') {
        updateNotes(ns => ns.filter(n => n.id !== prev.id));
      } else {
        updateNotes(ns => ns.map(n => n.id === prev.id ? { ...n, text: prev.text } : n));
      }
      return { id: null, text: '' };
    });
  };

  const cancel = () => {
    skipBlur.current = true;
    setEditing(prev => {
      if (prev.id && prev.text === '') {
        updateNotes(ns => ns.filter(n => n.id !== prev.id));
      }
      return { id: null, text: '' };
    });
  };

  const onChangeEditing = (e) => {
    const v = e.target.value;
    setEditing(prev => ({ ...prev, text: v }));
    autoResize(e.target);
  };

  const handleKeyDown = (e) => {
    const el = e.target;
    const caret = el.selectionStart;
    const caretEnd = el.selectionEnd;

    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
      return;
    }

    if (e.key === 'Enter') {
      const text = editing.text;
      const isEmpty = text.length === 0;
      const prevIsNewline = caret > 0 && text[caret - 1] === '\n';

      // Enter on an empty bullet → finalize: drop the empty bullet + exit edit mode
      if (isEmpty) {
        e.preventDefault();
        const curId = editing.id;
        skipBlur.current = true;
        updateNotes(ns => ns.filter(n => n.id !== curId));
        setEditing({ id: null, text: '' });
        return;
      }

      // Enter right after a newline → split into a new bullet below
      if (prevIsNewline) {
        e.preventDefault();
        const splitStart = caret - 1;
        const left = text.slice(0, splitStart);
        const right = text.slice(caret);
        const newId = Date.now() + Math.random();
        skipBlur.current = true;
        updateNotes(ns => {
          const i = ns.findIndex(n => n.id === editing.id);
          if (i < 0) return ns;
          const out = [...ns];
          out[i] = { ...out[i], text: left };
          out.splice(i + 1, 0, { id: newId, text: right, addedAt: Date.now() });
          return out;
        });
        pendingCaret.current = 0;
        setEditing({ id: newId, text: right });
        return;
      }
      // otherwise: default newline
    }

    if (e.key === 'Backspace' && caret === 0 && caretEnd === 0) {
      const i = notes.findIndex(n => n.id === editing.id);
      if (i < 0) return;
      if (i === 0) {
        if (editing.text === '' && notes.length > 1) {
          e.preventDefault();
          const next = notes[1];
          skipBlur.current = true;
          updateNotes(ns => ns.filter(n => n.id !== editing.id));
          pendingCaret.current = 0;
          setEditing({ id: next.id, text: next.text });
        }
        return;
      }
      e.preventDefault();
      const prev = notes[i - 1];
      const mergePoint = prev.text.length;
      const mergedText = prev.text + editing.text;
      skipBlur.current = true;
      updateNotes(ns => {
        const j = ns.findIndex(n => n.id === editing.id);
        if (j <= 0) return ns;
        const out = [...ns];
        out[j - 1] = { ...out[j - 1], text: mergedText };
        out.splice(j, 1);
        return out;
      });
      pendingCaret.current = mergePoint;
      setEditing({ id: prev.id, text: mergedText });
    }
  };

  const removeNote = (id) => {
    if (editing.id === id) { skipBlur.current = true; setEditing({ id: null, text: '' }); }
    updateNotes(ns => ns.filter(n => n.id !== id));
  };

  const textSize = 'text-base';
  const fmtAdded = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  };
  const listMax = 'max-h-64';

  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 flex flex-col space-y-2 fade-in group/card">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium" style={{ color: 'var(--theme-strong)' }}>
        <StickyNote size={14} className="flex-shrink-0" />
        {editingTitle ? (
          <input
            ref={titleInputRef}
            value={titleDraft}
            onChange={e => setTitleDraft(e.target.value)}
            onKeyDown={onTitleKeyDown}
            onBlur={commitTitle}
            placeholder="List title…"
            className="flex-1 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5 uppercase tracking-wider text-[11px] focus:outline-none focus:border-stone-400"
            style={{ color: 'var(--theme-strong)' }}
          />
        ) : (
          <span
            onClick={startEditTitle}
            className="flex-1 cursor-text truncate"
          >
            {group.title || 'Untitled'}
          </span>
        )}
        <button
          onClick={onDelete}
          className="opacity-0 group-hover/card:opacity-100 text-stone-300 hover:text-red-600 transition flex-shrink-0"
          aria-label="Delete list"
          tabIndex={-1}
        >
          <X size={12} />
        </button>
      </div>
      <ul className={`${listMax} overflow-y-auto scrollbar-thin space-y-1 pr-1`}>
        {notes.map((n) => (
          <li key={n.id} className={`group flex items-start gap-2 ${textSize} text-stone-700`}>
            <button
              type="button"
              onClick={() => updateNotes(ns => ns.map(nn => nn.id === n.id ? { ...nn, done: !nn.done } : nn))}
              className={`mt-0.5 flex-shrink-0 leading-snug transition ${n.done ? 'text-emerald-800' : 'text-stone-300 hover:text-emerald-800'}`}
              aria-label={n.done ? 'Mark undone' : 'Mark done'}
              tabIndex={-1}
            >
              {n.done ? <Check size={11} /> : '•'}
            </button>
            {editing.id === n.id ? (
              <textarea
                ref={textareaRef}
                value={editing.text}
                onChange={onChangeEditing}
                onKeyDown={handleKeyDown}
                onBlur={commit}
                rows={1}
                className={`flex-1 ${textSize} bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5 resize-none overflow-hidden focus:outline-none focus:border-stone-400 leading-snug`}
              />
            ) : (
              <span
                className={`flex-1 leading-snug whitespace-pre-wrap break-words cursor-text ${n.done ? 'text-stone-400 line-through' : ''}`}
                onClick={() => startEdit(n)}
              >
                {n.text}
              </span>
            )}
            {n.addedAt && (
              <span className="text-[11px] text-stone-300 italic font-light flex-shrink-0 mt-1 leading-snug tabular-nums">
                {fmtAdded(n.addedAt)}
              </span>
            )}
            <button
              onClick={() => removeNote(n.id)}
              className="opacity-0 group-hover:opacity-100 text-stone-300 hover:text-stone-600 transition flex-shrink-0 mt-1"
              aria-label="Remove note"
              tabIndex={-1}
            >
              <X size={12} />
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={startNewBullet}
        className={`${textSize} text-stone-400 italic hover:text-stone-600 transition text-left flex items-center gap-2`}
      >
        <span className="text-stone-300 flex-shrink-0">•</span>
        <span>Add a note…</span>
      </button>
      {notes.length > 0 && (
        <button
          onClick={clearAll}
          className="text-[11px] text-stone-400 hover:text-red-600 transition self-start"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

function ChatPanel({ dayKey, messages, input, setInput, send, loading, error, onAddItem, onScan, scanLoading, scanError, onClear, chatEndRef, mobileOnly }) {
  const fileRef = useRef(null);
  return (
    <div className={`bg-white border border-stone-200 rounded-xl flex flex-col ${mobileOnly ? 'h-[70vh]' : 'h-[520px]'}`}>
      <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
        <Sparkles size={14} className="text-emerald-800" />
        <span className="text-[10px] uppercase tracking-wider text-stone-400 font-medium">Claude · context: {dayKey}</span>
        <button
          onClick={onClear}
          disabled={!messages.length}
          title="Clear chat thread"
          className="ml-auto text-stone-300 hover:text-red-500 disabled:opacity-30 disabled:hover:text-stone-300 transition"
        >
          <Trash2 size={13} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-xs text-stone-400 italic text-center py-8">
            Start a check-in. Tell Claude what you worked on, what's next, or ask for a strategic read.
          </div>
        )}
        {messages.map((m, i) => {
          const prev = i > 0 ? messages[i - 1] : null;
          const showDate = m.date && (!prev || prev.date !== m.date);
          return (
            <React.Fragment key={i}>
              {showDate && (
                <div className="flex items-center gap-2 py-1">
                  <div className="flex-1 h-px bg-stone-100" />
                  <span className="text-[10px] uppercase tracking-wider text-stone-400 font-medium">{m.date}</span>
                  <div className="flex-1 h-px bg-stone-100" />
                </div>
              )}
              <Message message={m} onAddItem={onAddItem} />
            </React.Fragment>
          );
        })}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-stone-400">
            <Loader2 size={12} className="animate-spin" /> Thinking…
          </div>
        )}
        {scanLoading && (
          <div className="flex items-center gap-2 text-xs text-stone-400">
            <Loader2 size={12} className="animate-spin" /> Reading your calendar photo…
          </div>
        )}
        {error && <div className="text-xs text-red-600">{error}</div>}
        {scanError && <div className="text-xs text-red-600">Scan: {scanError}</div>}
        <div ref={chatEndRef} />
      </div>
      <div className="p-3 border-t border-stone-100 flex gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files && e.target.files[0];
            e.target.value = '';
            if (f && onScan) await onScan(f);
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current && fileRef.current.click()}
          disabled={scanLoading || loading}
          title="Scan a paper calendar"
          className="px-3 bg-stone-100 text-stone-700 rounded-lg hover:bg-stone-200 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {scanLoading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
        </button>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Check in, ask, plan…"
          className="flex-1 text-sm px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg resize-none focus:outline-none focus:border-stone-400 max-h-24"
          rows={1}
        />
        <button
          onClick={send}
          disabled={loading || !input.trim()}
          className="px-3 bg-emerald-900 text-white rounded-lg hover:bg-emerald-800 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}

function Message({ message, onAddItem }) {
  const isUser = message.role === 'user';
  const items = !isUser ? extractListItems(message.content, message.date) : [];
  const [appliedItems, setAppliedItems] = useState(new Set());
  return (
    <div className={`fade-in ${isUser ? 'flex justify-end' : ''}`}>
      <div className={`${isUser ? 'bg-stone-900 text-white max-w-[85%]' : 'text-stone-800 max-w-full'} rounded-2xl ${isUser ? 'px-3.5 py-2' : ''}`}>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</div>
        {!isUser && items.length > 0 && (
          <div className="mt-2 pt-2 border-t border-stone-100 space-y-1">
            <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-1">Add to calendar:</div>
            {items.map((it, i) => {
              const isApplied = appliedItems.has(i);
              const badge = formatDateBadge(it.dates);
              return (
                <button
                  key={i}
                  disabled={isApplied}
                  onClick={() => { onAddItem(it.text, it.dates); setAppliedItems(prev => new Set(prev).add(i)); }}
                  style={{ backgroundColor: isApplied ? '#2E3F2E' : 'var(--theme-tint)' }}
                  onMouseEnter={(e) => { if (!isApplied) e.currentTarget.style.backgroundColor = 'var(--theme-accent)'; }}
                  onMouseLeave={(e) => { if (!isApplied) e.currentTarget.style.backgroundColor = 'var(--theme-tint)'; }}
                  className={`w-full text-left text-xs px-2 py-1 rounded flex items-start gap-1.5 ${
                    isApplied ? 'text-white cursor-default' : 'text-emerald-900 group'
                  }`}
                >
                  {isApplied ? <Check size={11} className="flex-shrink-0 mt-0.5" /> : <Plus size={11} className="flex-shrink-0 mt-0.5" />}
                  <span className="flex-1 leading-snug">
                    {badge && (
                      <span className={`font-medium mr-1.5 tabular-nums ${isApplied ? 'text-emerald-100' : 'text-emerald-700/70'}`}>{badge}</span>
                    )}
                    <span>{it.text}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
